import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import { registerUser, loginUser } from './api/api'
import { Role } from '@/auth/role'
import { readStoredTheme, resolveTheme, systemPrefersDark, THEME_STORAGE_KEY } from '@/utils/theme'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    status: '',
    token: localStorage.getItem('token') || '',
    user : JSON.parse(localStorage.getItem('user')) || {},
    pageSize: 25,
    filter: '',
    modalTitle: '',
    modalComponent: '',
    isModalVisible: false,
    isConfirmVisible: false,
    isLoading: false,
    id: '',
    isDeleteQuestion: false,
    systemId: null,
    deleteAction: function() {},
    callback: function() {},
    dashboardStatus: null,
    serviceDetails: null,
    avatar: null,
    acceptedCookies: false,
    language: 'English',
    isMobile: false,
    hasTasks: false,
    hasTickets: false,
    theme: readStoredTheme(),
    systemPrefersDark: systemPrefersDark()
  },
  mutations: {
    change_theme(state, value) {
      state.theme = value
      localStorage.setItem(THEME_STORAGE_KEY, value)
    },
    change_systemPrefersDark(state, value) {
      state.systemPrefersDark = value
    },
    change_cookies(state, val) {
      state.acceptedCookies = val
      // console.log('change cookies: ' + val)
      localStorage.setItem('accCookies', val)
    },
    auth_request(state){
      state.status = 'loading'
    },
    auth_success(state, user){
      state.status = 'success'
      state.token = user.token
      state.user = user
    },
    auth_error(state){
      state.status = 'error'
    },
    logout(state){
      state.status = ''
      state.token = ''
      state.user = {}
    },
    update_profile(state, fullName) {
      state.user.fullName = fullName
    },
    change_page_size(state, pageSize) {
      state.pageSize = pageSize
    },
    change_filter(state, filter) {
      state.filter = filter
    },
    change_systemId(state, id) {
      state.systemId = id
    },
    change_dashboardStatus(state, status) {
      state.dashboardStatus = status
    },
    show_modal(state, {modalTitle, modalComponent, callback, id}) {
      state.modalTitle = modalTitle
      state.modalComponent = modalComponent
      state.id = id
      state.callback = callback
      state.isModalVisible = true
    },
    hide_modal(state) {
      state.modalTitle = ''
      state.modalComponent = ''
      state.id = ''
      state.callback = function() {}
      state.isModalVisible = false
    },
    show_delete_confirm(state, deleteAction) {
      state.isDeleteQuestion = true
      state.deleteAction = deleteAction
    },
    hide_delete_confirm(state) {
      state.isDeleteQuestion = false
      state.deleteAction = function() {}
    },
    show_confirm(state) {
      state.isConfirmVisible = true
    },
    hide_confirm(state) {
      state.isConfirmVisible = false
    },
    set_serviceDetails(state, service) {
      state.serviceDetails = service
    },
    update_avatar(state, avatar){
      state.avatar = avatar
    },
    set_loading(state, value){
      state.isLoading = value
    },
    change_language(state, value) {
      state.language = value
      localStorage.setItem('lang', value)
      // this.$root.$i18n.locale = value
    },
    change_isMobile(state, value) {
      state.isMobile = value
    },
    system_hasTasks(state, value) {
      state.hasTasks = value
    },
    system_hasTickets(state, value) {
      state.hasTickets = value
    },
    change_systemTableShown(state, value) {
      localStorage.setItem('systemTableShownColumns', JSON.stringify(value))
    }
  },
  actions: {
    change_theme({commit}, value) {
      commit('change_theme', value)
    },
    set_isMobile({commit}, value) {
      commit('change_isMobile', value)
    },
    login({commit}, user){
        return new Promise((resolve, reject) => {
          commit('auth_request')
          loginUser(user)
            .then(resp => {
              if(["Server", "PC"].includes(resp.data.roles[0])) {
                console.log(resp.data.roles[0])
                commit('auth_error')
                localStorage.removeItem('token')
                localStorage.removeItem('user')
                reject('Unauthorized access')
              }
              const token = resp.data.token
              localStorage.setItem('token', `Bearer ${token}`)
              localStorage.setItem('user', JSON.stringify(resp.data))
              axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
              commit('auth_success', resp.data)
              resolve(resp)
            })
            .catch(err => {
              commit('auth_error')
              localStorage.removeItem('token')
              localStorage.removeItem('user')
              reject(err)
            })
        })
    },
    register({commit}, user){
      return new Promise((resolve, reject) => {
        commit('auth_request')
        registerUser(user)
          .then(resp => {
            const token = resp.data.token
            localStorage.setItem('token', `Bearer ${token}`)
            localStorage.setItem('user', JSON.stringify(resp.data))
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
            commit('auth_success', resp.data)
            resolve(resp)
          })
          .catch(err => {
            commit('auth_error', err)
            localStorage.removeItem('token')
            localStorage.removeItem('user')
            reject(err)
          })
      })
    },
    logout({commit}){
      return new Promise((resolve, reject) => {
        commit('logout')
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        delete axios.defaults.headers.common['Authorization']
        resolve()
      })
    },
    change_language({commit}, languageName) {
      commit('change_language', languageName)
    },
    update_cookies({commit}, val) {
      commit('change_cookies', val)
    },
    update_profile({commit}, fullName){
      commit('update_profile', fullName)
    },
    change_page_size({commit}, pageSize) {
      commit('change_page_size', pageSize)
    },
    change_filter({commit}, filter) {
      commit('change_filter', filter)
    },
    change_systemId({commit}, id) {
      commit('change_systemId', id)
    },
    show_modal({commit}, {modalTitle, modalComponent, callback, id}) {
      commit('show_modal', {modalTitle, modalComponent, callback, id})
    },
    hide_modal({commit}) {
      commit('hide_modal')
    },
    show_delete_confirm({commit}, deleteAction) {
      commit('show_delete_confirm', deleteAction)
    },
    hide_delete_confirm({commit}) {
      commit('hide_delete_confirm')
    },
    change_dashboardStatus({commit}, status) {
      commit('change_dashboardStatus', status)
    },
    show_confirm({commit}) {
      commit('show_confirm')
    },
    hide_confirm({commit}) {
      commit('hide_confirm')
    },
    set_serviceDetails({commit}, service){
      commit('set_serviceDetails', service)
    },
    update_avatar({commit}, avatar) {
      commit('update_avatar', avatar)
    },
    set_loading({commit}, value) {
      commit('set_loading', value)
    },
    system_hasTasks({commit}, value) {
      commit('system_hasTasks', value)
    },
    system_hasTickets({commit}, value) {
      commit('system_hasTickets', value)
    },
    change_systemTableShown({commit}, value) {
      commit('change_systemTableShown', value)
    }
  },
  getters : {
    theme: state => state.theme,
    // 'light' | 'dark' — wybór 'system' rozwiązany według ustawień systemu.
    resolvedTheme: state => resolveTheme(state.theme, state.systemPrefersDark),
    isLoading: state => state.isLoading,
    isLoggedIn: state => !!state.token,
    authStatus: state => state.status,
    user: state => state.user,
    isAdmin: state => state.user.roles != null && state.user.roles.includes(Role.Admin),
    isService: state => state.user.roles != null && state.user.roles.includes(Role.Service),
    isUser: state => state.user.roles != null && state.user.roles.includes(Role.User),
    pageSize: state => state.pageSize,
    filter: state => state.filter,
    isModalVisible: state => state.isModalVisible,
    isConfirmVisible: state => state.isConfirmVisible,
    modalTitle: state => state.modalTitle,
    modalComponent: state => state.modalComponent,
    id: state => state.id,
    isDeleteQuestion: state => state.isDeleteQuestion,
    deleteAction: state => state.deleteAction,
    callback: state => state.callback,
    systemId: state => state.systemId,
    dashboardStatus: state => state.dashboardStatus,
    serviceDetails: state => state.serviceDetails,
    avatar: state => state.avatar,
    cookies: state => {
      var c = localStorage.getItem('accCookies')
      state.acceptedCookies = c == undefined || c == 'false' ? false : true
      return state.acceptedCookies
    },
    language: state => {
      var lang = localStorage.getItem('lang')
      state.language = lang == undefined ? 'English' : lang
      return state.language
    },
    isMobile: state => state.isMobile,
    hasTasks: state => state.hasTasks,
    hasTickets: state => state.hasTickets,
    systemTableShownColumns: state => {
      var columns = localStorage.getItem('systemTableShownColumns')
      if(columns == undefined){
        return undefined;
      }
      try{
        var arr = JSON.parse(columns)
        return arr
      } catch (ex) {
        localStorage.removeItem('systemTableShownColumns')
        return undefined;
      }
    }
  }
})



// WEBPACK FOOTER //
// ./src/store.js